import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { createPortal } from 'react-dom';

interface ConfirmationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (password?: string) => void;
  title?: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  confirmButtonStyle?: 'primary' | 'danger' | 'warning';
  isLoading?: boolean;
  /** When true, shows a password field and passes its value to onConfirm. Confirm disabled until password is non-empty. */
  requirePassword?: boolean;
  passwordLabel?: string;
  passwordPlaceholder?: string;
}

const ConfirmationModal: React.FC<ConfirmationModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  confirmText,
  cancelText,
  confirmButtonStyle = 'primary',
  isLoading = false,
  requirePassword = false,
  passwordLabel,
  passwordPlaceholder,
}) => {
  const { t } = useTranslation();
  const [password, setPassword] = useState('');

  useEffect(() => {
    if (isOpen && requirePassword) setPassword('');
  }, [isOpen, requirePassword]);

  const defaultTitle = title || t('common.confirm');
  const defaultConfirmText = confirmText || t('common.confirm');
  const defaultCancelText = cancelText || t('common.cancel');
  if (!isOpen) return null;

  const handleConfirm = () => {
    if (requirePassword) {
      onConfirm(password);
    } else {
      onConfirm();
    }
  };

  const canConfirm = !requirePassword || password.trim().length > 0;

  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget && !isLoading) {
      onClose();
    }
  };

  const getConfirmButtonClass = () => {
    switch (confirmButtonStyle) {
      case 'danger':
        return 'bg-red-600 hover:bg-red-700 text-white';
      case 'warning':
        return 'bg-yellow-600 hover:bg-yellow-700 text-white';
      case 'primary':
      default:
        return 'bg-primary hover:bg-blue-700 text-white';
    }
  };

  const modalContent = (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 z-[9998] transition-opacity"
        onClick={handleBackdropClick}
        style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, width: '100%', height: '100%' }}
      ></div>

      {/* Modal */}
      <div
        className="fixed inset-0 z-[9999] flex items-center justify-center p-2 sm:p-4"
        style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, width: '100%', height: '100%' }}
      >
        <div
          className="bg-white dark:bg-gray-800 rounded-lg shadow-xl border border-gray-200 dark:border-gray-700 w-full max-w-md overflow-hidden flex flex-col"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Modal Header */}
          <div className="px-4 lg:px-6 py-3 lg:py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between flex-shrink-0">
            <h2 className="text-lg lg:text-xl font-semibold text-gray-900 dark:text-gray-100">
              {defaultTitle}
            </h2>
            {!isLoading && (
              <button
                onClick={onClose}
                className="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
              >
                <i className="fa-solid fa-times text-xl"></i>
              </button>
            )}
          </div>

          {/* Modal Content */}
          <div className="px-4 lg:px-6 py-3 lg:py-4 flex-1 space-y-4">
            <p className="text-gray-700 dark:text-gray-300">
              {message}
            </p>
            {requirePassword && (
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                  {passwordLabel ?? t('common.password')}
                </label>
                <input
                  type="text"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && canConfirm && handleConfirm()}
                  placeholder={passwordPlaceholder}
                  autoComplete="off"
                  autoFocus
                  className="w-full px-4 py-2.5 border border-gray-200 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-700/50 text-gray-900 dark:text-gray-100 placeholder-gray-400 focus:ring-2 focus:ring-primary/30 focus:border-primary outline-none input-mask-password"
                />
              </div>
            )}
          </div>

          {/* Modal Footer */}
          <div className="px-4 lg:px-6 py-3 lg:py-4 bg-gray-50 dark:bg-gray-900 border-t border-gray-200 dark:border-gray-700 rounded-b-lg">
            <div className="flex items-center justify-end space-x-2">
              <button
                type="button"
                onClick={onClose}
                disabled={isLoading}
                className="px-4 py-2 text-sm text-gray-700 dark:text-gray-300 font-medium rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {defaultCancelText}
              </button>
              <button
                type="button"
                onClick={handleConfirm}
                disabled={isLoading || !canConfirm}
                className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${getConfirmButtonClass()}`}
              >
                {isLoading ? (
                  <>
                    <i className="fa-solid fa-spinner fa-spin mr-1.5"></i>
                    {t('common.processing')}
                  </>
                ) : (
                  defaultConfirmText
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );

  // Render modal to body using portal
  return createPortal(modalContent, document.body);
};

export default ConfirmationModal;

